import React from 'react';
import * as Sentry from '@sentry/react';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {Button} from '@chekinapp/ui';
import {IV_DOCUMENT_IMAGE_TYPES, IV_PHOTO_SOURCE} from '@chekin/core';
import {useSummary} from 'context/summary';
import {useDocumentSelfieDetection, usePath} from 'hooks';
import {useScanDocumentKiosko} from 'hooks/kiosko';
import {useGetGuestId} from 'hooks/useGetGuestId';
import {PATHS} from 'routes/constants';
import {ScanDocument, useIVStore, ANALYTIC_EVENTS, Analytics} from '@chekin/shared';
import {KioskoErrorModal} from 'components/common/KioskoErrorModal';
import type {KioskoScanData} from 'context/kiosko';
import {KIOSKO_BUTTON_CLASSES} from 'utils/kioskoClasses';

function KioskoScanDocumentView() {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {reservationId, countryCode, data: summary} = useSummary();
  const {resetAllIVState} = useIVStore();
  const guestId = useGetGuestId();
  const {getMainPath} = usePath();

  const {ProcessModal, ErrorModal, RetryModal, isProcessing, sendAndReadImageForOCR} =
    useDocumentSelfieDetection({
      docType: IV_DOCUMENT_IMAGE_TYPES.back,
      onlyScanning: true,
    });

  const handleDocumentScanned = React.useCallback(
    (data: KioskoScanData | null) => {
      if (!countryCode) {
        Sentry.captureMessage('countryCode is missing in KioskoScanDocumentView', {
          extra: {summary},
        });
        return;
      }
      sendAndReadImageForOCR({
        image: data?.image_base64 || '',
        source: IV_PHOTO_SOURCE.appCamera,
        countryCode,
        reservationId,
      });
    },
    [countryCode, reservationId, sendAndReadImageForOCR, summary],
  );

  const {scanDocument} = useScanDocumentKiosko({
    onDocumentScanned: handleDocumentScanned,
  });

  const handleScan = React.useCallback(() => {
    Analytics.track(ANALYTIC_EVENTS.ivPhotoTaken, {'Used camera': 'app'});
    scanDocument({side: IV_DOCUMENT_IMAGE_TYPES.front});
  }, [scanDocument]);

  const handleSkip = React.useCallback(() => {
    resetAllIVState();
    Analytics.track(ANALYTIC_EVENTS.ivSkipping);
    navigate(`${getMainPath(PATHS.addPersonalDataForm)}/${guestId || ''}`);
  }, [resetAllIVState, navigate, getMainPath, guestId]);

  return (
    <>
      <ScanDocument />
      {!isProcessing && (
        <div className="flex flex-col items-center gap-8 kiosko:mt-[114px]">
          <Button onClick={handleScan} className={KIOSKO_BUTTON_CLASSES}>
            {t('scan_document')}
          </Button>
          <Button
            variant="secondary"
            onClick={handleSkip}
            className={KIOSKO_BUTTON_CLASSES}
          >
            {t('skip_process')}
          </Button>
        </div>
      )}
      <ProcessModal />
      <ErrorModal />
      <RetryModal />
      <KioskoErrorModal />
    </>
  );
}

export {KioskoScanDocumentView};
